import React, { useState, useEffect } from "react";
import { AxiosError } from "axios";
import { format } from "date-fns";
import { useDispatch } from "react-redux";
import { useQuery } from "react-query";
import {
  useParams,
  Link,
  useSearchParams,
  generatePath,
  useNavigate,
} from "react-router-dom";
import { authAxios as axios, checkIsAgent } from "shared-auth";
import {
  ActionList,
  BreadcrumbBar,
  NotificationToast,
  PageHeader,
  Head,
  myAccountLink,
  Spinner,
} from "component-library";
import { useTranslation, Trans } from "i18n";
import { DatesFormat, ErrorCode, ISelfTapeRequestStatus } from "shared-types";
import { dataTestIds } from "data-testids";
import { ReactComponent as PrintIcon } from "component-library/src/images/svg/icons/print.svg";
import { ReactComponent as ArrowIcon } from "component-library/src/images/svg/icons/arrow.svg";
import { setLoading } from "store/ui/ui.actions";
import SelfTapeRequestInfo from "components/SelfTapeRequestInfo/SelfTapeRequestInfo";
import config from "config/global";
import SelfTapeRequestActionFooter from "pages/SelfTapeRequestPage/SelfTapeRequestActionFooter";
import AcceptRequestModal from "pages/SelfTapeRequestPage/AcceptRequestModal";
import ForbiddenRequest from "./ForbiddenRequest";
import { ISelfTapeRequest } from "shared/models/selfTapeRequest";
import { selfTaperRequestQueryKey } from "pages/SelfTapeRequestPage/constants";
import "./SelfTapeRequestPage.scss";
import { ROLE_FULL_PATH, SELF_TAPE_FULL_PATH } from "config/common";

const showSuccessParam = "showSelfTapesSendingSuccess";

const SelfTapeRequestPage = () => {
  const { performerRequestId } = useParams<{
    performerRequestId: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [notification, setNotification] = useState(false);
  const [acceptRequestModalIsOpen, setAcceptRequestModalIsOpen] =
    useState(false);

  const {
    data: selfTapeRequest,
    isLoading: isLoadingSubmission,
    error,
  } = useQuery<ISelfTapeRequest, AxiosError>(
    [selfTaperRequestQueryKey, performerRequestId],
    async () => {
      const { data } = await axios.get(
        `${config.selfTapesApiUrl}/submission/performerrequest/${performerRequestId}`
      );

      return data;
    }
  );

  useEffect(() => {
    if (searchParams.get(showSuccessParam)) {
      setSearchParams({}, { replace: true });
      setNotification(true);
    }

    dispatch(setLoading(false));
  }, [dispatch, searchParams, setSearchParams]);

  if (isLoadingSubmission) return <Spinner />;

  const isAgent = checkIsAgent();
  const clientName = selfTapeRequest?.performer?.name || "";
  const sendByName = selfTapeRequest?.castingDirector?.email
    ? `${selfTapeRequest?.castingDirector.name} (${selfTapeRequest?.castingDirector?.email})`
    : selfTapeRequest?.castingDirector.name;

  return (
    <section className="c-self-tape-request-page break-all">
      <Head>
        <title>{selfTapeRequest?.title}</title>
      </Head>
      <BreadcrumbBar
        items={[
          {
            text: t("common:nav.links.home"),
            url: myAccountLink,
          },
          {
            text: t("common:mediaRequest.page.breadcrumbs.mediaRequest"),
            url: SELF_TAPE_FULL_PATH,
          },
        ]}
      />

      {error?.response?.status === ErrorCode.Forbidden && <ForbiddenRequest />}

      {selfTapeRequest && (
        <>
          <PageHeader
            pageTitle={
              <>
                <h1
                  data-testid={
                    dataTestIds.nuJobsApp["SelfTapeRequest.Title.header"]
                  }
                >
                  {t("common:selfTapes.request.header.title", {
                    role: selfTapeRequest.roles[0].name,
                  })}
                  {isAgent ? ` - ${clientName}` : ""}
                </h1>
                <div className="c-self-tape-request-page__request-data-container">
                  <div className="c-self-tape-request-page__request-data">
                    {t("common:selfTapes.request.header.sentBy", {
                      name: sendByName,
                      time: format(
                        new Date(selfTapeRequest.createdAt),
                        DatesFormat.dateTimeFormatPrimary
                      ),
                    })}{" "}
                    {isAgent &&
                      t("common:suggestions.suggestedBy", {
                        name: selfTapeRequest.suggestedBy,
                      })}
                  </div>
                  <div className="c-self-tape-request-page__request-data">
                    {t("common:selfTapes.request.header.dueBy", {
                      time: format(
                        new Date(selfTapeRequest.expiryDate),
                        DatesFormat.dateTimeFormatPrimary
                      ),
                    })}
                  </div>
                  <div
                    className="c-self-tape-request-page__request-data"
                    data-testid={
                      dataTestIds.nuJobsApp[
                        "SelfTapeRequest.Title.breakDownTitle"
                      ]
                    }
                  >
                    <Trans
                      i18nKey="common:selfTapes.request.header.breakdown"
                      components={{
                        tag: isAgent ? (
                          <span />
                        ) : (
                          <Link
                            to={generatePath(ROLE_FULL_PATH, {
                              breakdownId: selfTapeRequest.breakdownId,
                              roleId: selfTapeRequest.rolesIds[0],
                            })}
                            className="text-color-neutral-one hover:underline"
                          />
                        ),
                      }}
                      values={{ name: selfTapeRequest.breakdownTitle }}
                    />
                  </div>
                </div>
              </>
            }
          />
          <PageHeader
            className="print:hidden"
            actionSection={
              <div>
                <ActionList
                  items={[
                    {
                      variant: "primary",
                      text: t("common:selfTapes.request.header.action.back"),
                      svgIconStart: <ArrowIcon width={20} height={20} />,
                      onClick: () => navigate(SELF_TAPE_FULL_PATH),
                    },
                    {
                      variant: "primary",
                      text: t("common:action.print"),
                      svgIconStart: <PrintIcon width={20} height={20} />,
                      onClick: () => window.print(),
                    },
                  ]}
                  variant="pageHeader"
                />
              </div>
            }
          />
          <div className="m-auto w-full max-w-[1440px] flex-1 px-4">
            <SelfTapeRequestInfo
              submissionDate={selfTapeRequest.submissionDate}
              description={selfTapeRequest.description}
              videosRequired={selfTapeRequest.videosRequired}
              isTagminUser={selfTapeRequest.isTagminUser ?? false}
              anyAmountOfVideos={Boolean(selfTapeRequest.anyAmount)}
              attachments={selfTapeRequest.attachments}
              isPublished={selfTapeRequest.isPublished}
              submissionMediaList={selfTapeRequest.submissionMedia}
              openResponseModal={() => setAcceptRequestModalIsOpen(true)}
              selfTapeRequestStatus={selfTapeRequest.status}
              selfTapeRequestExpiryDate={selfTapeRequest.expiryDate}
              performerReplyTimestamp={selfTapeRequest.performerReplyTimestamp}
              performerReplyComments={selfTapeRequest.performerReplyComments}
              performerReplyBy={selfTapeRequest.performerReplyBy}
              mediaRequestType={selfTapeRequest.mediaRequestType}
              requestToClientComments={selfTapeRequest.requestToClientComments}
              requestToClientSentBy={selfTapeRequest.requestToClientSentBy}
              forwardedToPerformerTimestamp={
                selfTapeRequest.forwardedToPerformerTimestamp
              }
              requestToTagminSentBy={selfTapeRequest.requestToTagminSentBy}
              forwardedToTagminTimestamp={
                selfTapeRequest.forwardedToTagminTimestamp
              }
              submissionBy={selfTapeRequest.submissionBy}
              isAgent={isAgent}
            />
            <div className="g-content-area">
              <div className="g-col-lg-4">
                <NotificationToast
                  stickToComponent={false}
                  handleHide={() => setNotification(false)}
                  text={t("common:selfTapes.request.successNotification")}
                  show={notification}
                />
              </div>
            </div>
            {acceptRequestModalIsOpen && (
              <AcceptRequestModal
                selfTapeRequestId={selfTapeRequest.performerRequestId}
                performerReplyComments={selfTapeRequest.performerReplyComments}
                forwardedToPerformer={selfTapeRequest.forwardedToPerformer}
                isAgent={isAgent}
                isAccepted={
                  selfTapeRequest.status === ISelfTapeRequestStatus.Accepted
                }
                isRejected={
                  selfTapeRequest.status === ISelfTapeRequestStatus.Rejected
                }
                closeModal={() => setAcceptRequestModalIsOpen(false)}
              />
            )}
          </div>
          <SelfTapeRequestActionFooter
            selfTapeRequest={selfTapeRequest}
            setAcceptRequestModalIsOpen={setAcceptRequestModalIsOpen}
            clientName={clientName}
            isAgent={isAgent}
          />
        </>
      )}
    </section>
  );
};

export default SelfTapeRequestPage;
